import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import EmailIllustration from '../../assets/resetPassword.png'
import { useSnackbarManager } from '../../components/common/snackbar'
import { useResetPassword } from './api'
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai'

const FillEyeIcon = AiFillEye as React.ElementType
const FillEyeInvisibleIcon = AiFillEyeInvisible as React.ElementType

export default function ResetPassword() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbarManager()

  const { mutate, isLoading } = useResetPassword()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const handleResetClick = () => {
    if (!password || !confirmPassword) {
      enqueueSnackbar('Please fill in both fields.', { variant: 'warning' })
      return
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{6,}$/

    if (!passwordRegex.test(password)) {
      enqueueSnackbar(
        'Password must be at least 6 characters long and include uppercase, lowercase, number, and special character.',
        { variant: 'error' }
      )
      return
    }

    if (password !== confirmPassword) {
      enqueueSnackbar('Passwords do not match.', { variant: 'error' })
      return
    }

    if (id) {
      mutate(
        {
          id,
          token: id,
          password,
          password_confirmation: confirmPassword,
        },
        {
          onSuccess: (data: any) => {
            enqueueSnackbar(data.message || 'Password reset successfully!', {
              variant: 'success',
            })
            setTimeout(() => {
              navigate('/login')
            }, 2000)
          },
          onError: () => {
            enqueueSnackbar('Password reset failed. Please try again.', {
              variant: 'error',
            })
          },
        }
      )
    } else {
      enqueueSnackbar('Invalid reset link.', { variant: 'warning' })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-lg w-full max-w-xl text-left mb-[100px]">
        <img
          src={EmailIllustration}
          alt="Reset Password"
          className="w-[6rem] h-[6rem] mx-auto mb-6"
        />

        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center justify-center">
          Reset Your Password
        </h2>

        <div className="space-y-4 text-left">
          <div>
            <label className="block text-gray-700 text-sm mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 text-left"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {showPassword ? (
                  <FillEyeIcon className="h-5 w-5" />
                ) : (
                  <FillEyeInvisibleIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {showConfirmPassword ? (
                  <FillEyeIcon className="h-5 w-5" />
                ) : (
                  <FillEyeInvisibleIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="mt-6 bg-white0 hover:bg-orange-600 transition-all duration-200 text-white font-semibold text-sm py-3 px-6 rounded-lg w-full max-w-xs mx-auto ml-[80px]"
          onClick={handleResetClick}
          disabled={isLoading}
        >
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </button>
      </div>
    </div>
  )
}
